import { approxEqual, approxZero, clamp, EPSILON } from './epsilon';

/**
 * A 2D vector in **world space** (y-up).
 *
 * Two shapes, one API:
 *
 * - `Vec2` is the readonly value type. Everything that stores or passes coordinates
 *   uses it, so accidental mutation of shared state is a compile error.
 * - Every operation accepts an optional `out: MutVec2`. Passing one writes the result
 *   in place and allocates nothing — the render loop and the solver's inner loops rely
 *   on this (see ADR-0004: "no allocation inside the frame loop").
 *
 * Callers that do not pass `out` get a fresh object, which is what almost all
 * construction and test code wants.
 */
export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

/** Mutable vector, used only as an output parameter in hot paths. */
export interface MutVec2 {
  x: number;
  y: number;
}

export const ZERO: Vec2 = Object.freeze({ x: 0, y: 0 });
export const UNIT_X: Vec2 = Object.freeze({ x: 1, y: 0 });
export const UNIT_Y: Vec2 = Object.freeze({ x: 0, y: 1 });

export function vec2(x: number, y: number): Vec2 {
  return { x, y };
}

/** Writes `(x, y)` into `out` and returns it. */
function write(out: MutVec2 | undefined, x: number, y: number): Vec2 {
  if (out === undefined) return { x, y };
  out.x = x;
  out.y = y;
  return out;
}

export function clone(v: Vec2, out?: MutVec2): Vec2 {
  return write(out, v.x, v.y);
}

export function add(a: Vec2, b: Vec2, out?: MutVec2): Vec2 {
  return write(out, a.x + b.x, a.y + b.y);
}

export function subtract(a: Vec2, b: Vec2, out?: MutVec2): Vec2 {
  return write(out, a.x - b.x, a.y - b.y);
}

export function scale(v: Vec2, k: number, out?: MutVec2): Vec2 {
  return write(out, v.x * k, v.y * k);
}

export function negate(v: Vec2, out?: MutVec2): Vec2 {
  return write(out, -v.x, -v.y);
}

/** `a + b * k`. The workhorse of "walk along a direction" code; fused to avoid a temporary. */
export function addScaled(a: Vec2, b: Vec2, k: number, out?: MutVec2): Vec2 {
  return write(out, a.x + b.x * k, a.y + b.y * k);
}

export function dot(a: Vec2, b: Vec2): number {
  return a.x * b.x + a.y * b.y;
}

/**
 * 2D cross product — the z component of the 3D cross product.
 *
 * Sign tells you orientation: positive when `b` is counter-clockwise from `a`.
 * Magnitude is the area of the parallelogram they span. This single function
 * underpins side-of-line tests, polygon orientation and line intersection.
 */
export function cross(a: Vec2, b: Vec2): number {
  return a.x * b.y - a.y * b.x;
}

export function length(v: Vec2): number {
  return Math.hypot(v.x, v.y);
}

/** Squared length. Prefer this in comparisons — it avoids a square root. */
export function lengthSquared(v: Vec2): number {
  return v.x * v.x + v.y * v.y;
}

export function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function distanceSquared(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/**
 * Unit vector in the same direction.
 *
 * Returns `ZERO` for a zero-length input rather than `NaN`. Silent `NaN` propagation
 * through a dependency graph is far harder to diagnose than a zero vector, and callers
 * that care can test with `isZero` first.
 */
export function normalize(v: Vec2, out?: MutVec2): Vec2 {
  const len = Math.hypot(v.x, v.y);
  if (len <= EPSILON) return write(out, 0, 0);
  return write(out, v.x / len, v.y / len);
}

/** Rotates 90° counter-clockwise. Exact — no trigonometry involved. */
export function perpendicular(v: Vec2, out?: MutVec2): Vec2 {
  return write(out, -v.y, v.x);
}

export function rotate(v: Vec2, radians: number, out?: MutVec2): Vec2 {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return write(out, v.x * cos - v.y * sin, v.x * sin + v.y * cos);
}

export function rotateAround(v: Vec2, center: Vec2, radians: number, out?: MutVec2): Vec2 {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = v.x - center.x;
  const dy = v.y - center.y;
  return write(out, center.x + dx * cos - dy * sin, center.y + dx * sin + dy * cos);
}

export function lerp(a: Vec2, b: Vec2, t: number, out?: MutVec2): Vec2 {
  return write(out, a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t);
}

export function midpoint(a: Vec2, b: Vec2, out?: MutVec2): Vec2 {
  return write(out, (a.x + b.x) * 0.5, (a.y + b.y) * 0.5);
}

/** Angle of the vector from the positive x-axis, in `(-π, π]`. */
export function angle(v: Vec2): number {
  return Math.atan2(v.y, v.x);
}

/** Signed angle from `a` to `b`, in `(-π, π]`. Positive is counter-clockwise. */
export function angleBetween(a: Vec2, b: Vec2): number {
  return Math.atan2(cross(a, b), dot(a, b));
}

/** Unit vector at `radians` from the positive x-axis. */
export function fromAngle(radians: number, magnitude = 1, out?: MutVec2): Vec2 {
  return write(out, Math.cos(radians) * magnitude, Math.sin(radians) * magnitude);
}

/** Vector projection of `v` onto `onto`. Returns `ZERO` if `onto` is degenerate. */
export function project(v: Vec2, onto: Vec2, out?: MutVec2): Vec2 {
  const lenSq = lengthSquared(onto);
  if (lenSq <= EPSILON * EPSILON) return write(out, 0, 0);
  const k = dot(v, onto) / lenSq;
  return write(out, onto.x * k, onto.y * k);
}

/** Reflects `v` across the line through the origin with direction `axis`. */
export function reflect(v: Vec2, axis: Vec2, out?: MutVec2): Vec2 {
  const lenSq = lengthSquared(axis);
  if (lenSq <= EPSILON * EPSILON) return write(out, v.x, v.y);
  const k = (2 * dot(v, axis)) / lenSq;
  return write(out, axis.x * k - v.x, axis.y * k - v.y);
}

export function equals(a: Vec2, b: Vec2, epsilon: number = EPSILON): boolean {
  return approxEqual(a.x, b.x, epsilon) && approxEqual(a.y, b.y, epsilon);
}

export function isZero(v: Vec2, epsilon: number = EPSILON): boolean {
  return approxZero(v.x, epsilon) && approxZero(v.y, epsilon);
}

/** True when both components are finite — the guard against `NaN`/`Infinity` entering the scene. */
export function isFinite(v: Vec2): boolean {
  return Number.isFinite(v.x) && Number.isFinite(v.y);
}

export function isParallel(a: Vec2, b: Vec2, epsilon: number = EPSILON): boolean {
  return approxZero(cross(a, b), epsilon * Math.max(1, length(a) * length(b)));
}

export function isPerpendicular(a: Vec2, b: Vec2, epsilon: number = EPSILON): boolean {
  return approxZero(dot(a, b), epsilon * Math.max(1, length(a) * length(b)));
}

/** Caps the magnitude of `v` at `max`, preserving direction. */
export function clampLength(v: Vec2, max: number, out?: MutVec2): Vec2 {
  const len = length(v);
  if (len <= max || len <= EPSILON) return write(out, v.x, v.y);
  const k = max / len;
  return write(out, v.x * k, v.y * k);
}

/** Component-wise clamp against a min/max corner. */
export function clampTo(v: Vec2, min: Vec2, max: Vec2, out?: MutVec2): Vec2 {
  return write(out, clamp(v.x, min.x, max.x), clamp(v.y, min.y, max.y));
}
